import type { AnnouncementSlug } from "../../../../../api/src/shared/domain/announcement";
import type {
  AnnouncementActionId,
  AnnouncementDismissMethod,
} from "../../../features/announcements/types";
import { gaPushEvent } from "../base.analytics";

enum Event {
  ANNOUNCEMENT_VIEWED = "announcement_viewed",
  ANNOUNCEMENT_CTA_CLICKED = "announcement_cta_clicked",
  ANNOUNCEMENT_DISMISSED = "announcement_dismissed",
}

/**
 * The denominator of every announcement rate: the pop-up reached the screen.
 * Fired once per announcement through `useTrackOnMount`, keyed on the slug, so
 * a re-render or a remount never double-counts one impression.
 *
 * Every event here reports the SLUG and never a schedule row's id. The slug
 * names the announcement itself, so rescheduling one into another room, or
 * running it again later, keeps reporting under one name — and the id of a
 * schedule row no longer reaches the client at all.
 */
export function gaEventAnnouncementViewed(params: {
  slug: AnnouncementSlug;
  roomId: string | null;
}) {
  gaPushEvent(Event.ANNOUNCEMENT_VIEWED, {
    announcement_slug: params.slug,
    room_id: params.roomId,
  });
}

/**
 * The reader took the announcement up on its offer. `action` is the button's
 * stable id and not its label: a label is translated, and it changes with every
 * copy edit, so a funnel grouped on it splits by locale and breaks on rewording.
 */
export function gaEventAnnouncementCtaClicked(params: {
  slug: AnnouncementSlug;
  roomId: string | null;
  action: AnnouncementActionId;
}) {
  gaPushEvent(Event.ANNOUNCEMENT_CTA_CLICKED, {
    announcement_slug: params.slug,
    room_id: params.roomId,
    action: params.action,
  });
}

export function gaEventAnnouncementDismissed(params: {
  slug: AnnouncementSlug;
  roomId: string | null;
  method: AnnouncementDismissMethod;
}) {
  gaPushEvent(Event.ANNOUNCEMENT_DISMISSED, {
    announcement_slug: params.slug,
    room_id: params.roomId,
    method: params.method,
  });
}
